import React from 'react';
import { useLang } from '@/lib/LangContext';
import { TID } from '@/constants/testIds';
import { UserPlus, Package, Truck, PawPrint } from 'lucide-react';

const ICONS = [UserPlus, Package, Truck, PawPrint];
// Keep the first step on-brand orange so users immediately read it as the
// primary call-to-action; the remaining steps stay in the calmer palette.
const ACCENTS = ['#F25C05', '#0A4D8C', '#0f6e44', '#8B4FBF'];

/**
 * Modern connected-timeline. The first step ("Click Sign Up") is rendered as
 * a button so tapping it opens the Registration popup — the rest remain
 * passive cards.
 */
export default function HowItWorks({ onOpenSignup }) {
  const { t } = useLang();

  return (
    <section id="how" data-testid={TID.how} className="relative py-4 md:py-6">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="relative">
          {/* Connecting dashed track behind badges (desktop only) */}
          <div
            className="hidden lg:block absolute left-[12.5%] right-[12.5%] top-[40px] h-px"
            style={{
              backgroundImage: 'linear-gradient(to right, #0A4D8C 50%, transparent 0%)',
              backgroundSize: '10px 1px',
              backgroundRepeat: 'repeat-x',
              opacity: 0.3,
            }}
            aria-hidden
          />

          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {t.how.steps.map((s, i) => {
              const Icon = ICONS[i] || PawPrint;
              const color = ACCENTS[i] || '#F25C05';
              const isSignupStep = i === 0;

              // Step 1 → interactive, warm orange card that opens signup.
              // Others → passive informational cards.
              const containerClass = isSignupStep
                ? 'group relative text-left bg-gradient-to-br from-[#FFF4EB] to-[#FFE4CF] border border-[#F25C05]/35 rounded-2xl p-4 md:p-5 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(242,92,5,0.25)] transition-all duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F25C05]/50'
                : 'group relative bg-white border border-[#eadfce] rounded-2xl p-4 md:p-5 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(10,77,140,0.1)] transition-all duration-300';

              const body = (
                <>
                  <div className="flex items-center gap-3">
                    <div
                      className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-[0_8px_18px_rgba(0,0,0,0.08)] shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      <Icon size={22} strokeWidth={2.2} />
                      <span
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border-2 flex items-center justify-center text-[11px] font-extrabold"
                        style={{ color, borderColor: color }}
                      >
                        {s.n}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-[#05223D] text-sm md:text-[15px] leading-tight">
                      {s.t}
                    </h3>
                  </div>
                  <p className={`text-xs md:text-[13px] mt-3 leading-relaxed ${isSignupStep ? 'text-[#5A3613]' : 'text-[#465B70]'}`}>
                    {s.d}
                  </p>
                </>
              );

              if (isSignupStep) {
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={onOpenSignup}
                    data-testid={`how-step-${i}`}
                    className={containerClass}
                  >
                    {body}
                  </button>
                );
              }
              return (
                <div
                  key={i}
                  className={containerClass}
                  data-testid={`how-step-${i}`}
                >
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
